import { PageToolbar } from '@app/components/page-toolbar/PageToolbar';
import { reportNameMessages } from '@app/modules/audit/modules/reports/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ChipItem, ChipNavigationMui } from '@mi-front/ui';
import { FormattedMessage } from 'react-intl';
import { Redirect, useLocation } from 'react-router-dom';
import { SavedReportsBroadcastEvidenceList } from './broadcast-evidence/SavedReportsBroadcastEvidenceList';
import { SavedReportsGipList } from './gip/SavedReportsGipList';

export const SAVED_REPORTS_PATH_BASE = '/audit/saved-reports';

/** URL path segments for saved-report type chips (must not collide with UUID detail routes). */
export const SAVED_REPORT_CHIP_PATHS = {
  gip: 'AUDIT_GIP_STATUS',
  'gip-alt': 'AUDIT_GIP_ALTERNATIVE_REPORT',
  'broadcast-evidence': 'AUDIT_BROADCAST_EVIDENCE',
} as const;

export type SavedReportChipPath = keyof typeof SAVED_REPORT_CHIP_PATHS;

const BROADCAST_EVIDENCE_PATH = `${SAVED_REPORTS_PATH_BASE}/broadcast-evidence`;

/**
 * Path-based chip navigation for saved reports (same pattern as AuditMatchCategoryNavigator).
 * Broadcast Evidence is gated by `app__audit_reports_broadcast_evidence_enabled`.
 */
export function SavedReportsTypeNavigator() {
  const location = useLocation();
  const isBroadcastEvidenceEnabled = useFeatureIsOn(
    'app__audit_reports_broadcast_evidence_enabled',
  );

  const isAtBasePath =
    location.pathname === SAVED_REPORTS_PATH_BASE ||
    location.pathname === `${SAVED_REPORTS_PATH_BASE}/`;

  const isOnBroadcastEvidencePath =
    location.pathname === BROADCAST_EVIDENCE_PATH ||
    location.pathname === `${BROADCAST_EVIDENCE_PATH}/`;

  if (isAtBasePath || (isOnBroadcastEvidencePath && !isBroadcastEvidenceEnabled)) {
    return <Redirect to={`${SAVED_REPORTS_PATH_BASE}/gip`} />;
  }

  const chips: ChipItem[] = [
    {
      value: 'gip',
      label: <FormattedMessage {...reportNameMessages.AUDIT_GIP_STATUS} />,
      // Distinct keys remount GIP vs GIP alt (same component type in the same chip slot).
      content: <SavedReportsGipList key="AUDIT_GIP_STATUS" reportType="AUDIT_GIP_STATUS" />,
    },
    {
      value: 'gip-alt',
      label: <FormattedMessage {...reportNameMessages.AUDIT_GIP_ALTERNATIVE_REPORT} />,
      content: (
        <SavedReportsGipList
          key="AUDIT_GIP_ALTERNATIVE_REPORT"
          reportType="AUDIT_GIP_ALTERNATIVE_REPORT"
        />
      ),
    },
  ];

  if (isBroadcastEvidenceEnabled) {
    chips.push({
      value: 'broadcast-evidence',
      label: <FormattedMessage {...reportNameMessages.AUDIT_BROADCAST_EVIDENCE} />,
      content: <SavedReportsBroadcastEvidenceList />,
    });
  }

  return (
    <ChipNavigationMui
      pathBase={SAVED_REPORTS_PATH_BASE}
      defaultActiveChip="gip"
      Wrapper={PageToolbar}
      chips={chips}
    />
  );
}
